<!--
	Generic Entity Changes Wizard
	
	This component displays change history for any entity type in a modal dialog.
	It can work with both generic AuditExecutive methods and entity-specific executives.
	
	Usage Examples:
	
	// For Contractors (using specific executive and methods):
	<EntityChangesWizard
		:entity-type="EntityType.CONTRACTOR"
		:entity-id="contractorId"
		:show="showDialog"
		:changes="changes"
		:executive="contractExecutive"
		recent-changes-method-name="getContractorRecentChanges"
		page-visit-method-name="logContractorPageVisit"
		@close="handleClose"
	/>
	
	// For any entity (using generic methods):
	<EntityChangesWizard
		:entity-type="EntityType.USER"
		:entity-id="userId"
		:show="showDialog"
		@close="handleClose"
	/>
	
	// With custom title and pre-loaded changes:
	<EntityChangesWizard
		:entity-type="EntityType.CLIENT"
		:entity-id="clientId"
		:show="showDialog"
		:changes="preloadedChanges"
		title="Client Changes Since Last Week"
		:hide-mark-as-read="true"
		@close="handleClose"
	/>
-->
<template>
	<v-dialog v-model="showDialog" max-width="600px" persistent>
		<v-card class="entity-changes-wizard">
			<v-card-title class="wizard-header">
				<div class="header-content">
					<v-icon color="primary" size="24">mdi-information-outline</v-icon>
					<span class="title-text">{{ displayTitle }}</span>
					<v-chip size="small" color="orange" variant="outlined" class="beta-chip">
						BETA
					</v-chip>
				</div>
				<v-btn class="close-button" icon="mdi-close" variant="text" size="small" @click="closeWizard" />
			</v-card-title>
			
			<v-card-text class="wizard-content">
				<div v-if="loading" class="loading-container">
					<v-progress-circular indeterminate color="primary" />
					<span>{{ $t('text.loadingChanges') }}</span>
				</div>
				
				<div v-else-if="displayChanges.length === 0" class="no-changes">
					<v-icon size="48" color="grey">mdi-check-circle-outline</v-icon>
					<p>{{ $t('text.noRecentChanges') }}</p>
				</div>
				
				<div v-else class="changes-list">
					<p class="changes-intro">
						{{ getChangesIntroText() }}
					</p>
					
					<div v-for="change in displayChanges" :key="change.id" class="change-item">
						<div class="change-header">
							<div class="user-info">
								<v-avatar size="32" color="primary">
									<span class="avatar-text">{{ getUserInitials(change.userEmail) }}</span>
								</v-avatar>
								<div class="user-details">
									<span class="user-email">{{ change.userEmail }}</span>
									<span class="user-role">{{ getUserRoleText(change.userRole) }}</span>
								</div>
							</div>
							<div class="change-time">
								{{ formatDate(change.createdAt) }}
							</div>
						</div>
						
						<div class="change-summary">
							<div class="summary-text">{{ getLocalizedSummary(change.changeSummary) }}</div>
						</div>
						
						<div v-if="change.changedFields && change.changedFields.length > 0" class="changed-fields">
							<v-chip 
								v-for="field in change.changedFields" 
								:key="field" 
								size="small" 
								variant="outlined" 
								class="field-chip"
							>
								{{ getFieldDisplayName(field) }}
							</v-chip>
						</div>
					</div>
				</div>
			</v-card-text>
			
			<v-card-actions class="wizard-actions">
				<v-spacer />
				<v-btn variant="text" @click="closeWizard">
					{{ $t('text.dismiss') }}
				</v-btn>
				<v-btn color="primary" variant="flat" @click="markAsRead" v-if="!hideMarkAsRead">
					{{ $t('text.markAsRead') }}
				</v-btn>
			</v-card-actions>
		</v-card>
	</v-dialog>
</template>

<script>
import moment from 'moment'
import AuditExecutive from '../../../../api2/src/modules/audit/AuditExecutive'
import { EntityType } from '../../../../api2/src/modules/audit/audit.types'

export default {
	name: 'EntityChangesWizard',
	props: {
		entityType: {
			type: String,
			required: true,
			validator: (value) => Object.values(EntityType).includes(value)
		},
		entityId: {
			type: String,
			required: true
		},
		show: {
			type: Boolean,
			default: false
		},
		changes: {
			type: Array,
			default: null
		},
		title: {
			type: String,
			default: ''
		},
		hideMarkAsRead: {
			type: Boolean,
			default: false
		},
		// Optional custom executive for entity-specific operations
		executive: {
			type: Object,
			default: null
		},
		// Method name for getting recent changes (for mark as read functionality)
		recentChangesMethodName: {
			type: String,
			default: 'getEntityRecentChanges'
		},
		// Method name for logging page visits (for mark as read functionality)
		pageVisitMethodName: {
			type: String,
			default: 'logEntityPageVisit'
		}
	},
	data() {
		return {
			showDialog: false,
			loading: false,
			internalChanges: [],
			lastVisit: null,
			hasUnseenChanges: false
		}
	},
	computed: {
		// Use prop changes if provided, otherwise use internal changes
		displayChanges() {
			return this.changes || this.internalChanges
		},
		displayTitle() {
			if (this.title) return this.title
			
			// Generate default title based on entity type
			const entityDisplayName = this.getEntityDisplayName()
			return this.$t('text.entityChangesTitleSinceLastPageView', { entity: entityDisplayName }) || 
			       `${entityDisplayName} Changes`
		}
	},
	watch: {
		show: {
			immediate: true,
			handler(newVal) {
				this.showDialog = newVal
				if (newVal && !this.changes) {
					// Only load changes if not provided via props
					this.loadChanges()
				}
			}
		}
	},
	methods: {
		async loadChanges() {
			this.loading = true
			try {
				// Use provided executive or fallback to AuditExecutive
				const executive = this.executive || new AuditExecutive(this)
				
				let result
				if (this.recentChangesMethodName === 'getEntityRecentChanges') {
					// Generic method expects entityType as first parameter
					result = await executive[this.recentChangesMethodName](this.entityType, this.entityId)
				} else {
					// Legacy/specific methods expect entityId directly
					result = await executive[this.recentChangesMethodName](this.entityId)
				}
				
				this.internalChanges = result.changes
				this.lastVisit = result.lastVisit
				this.hasUnseenChanges = result.hasUnseenChanges
			} catch (error) {
				console.error(`Error loading ${this.entityType} changes:`, error)
				this.internalChanges = []
			} finally {
				this.loading = false
			}
		},
		
		async markAsRead() {
			try {
				// Use provided executive or fallback to AuditExecutive
				const executive = this.executive || new AuditExecutive(this)
				
				// Log the page visit with actual route to mark changes as read
				if (this.pageVisitMethodName === 'logEntityPageVisit') {
					// Generic method expects entityType as first parameter
					await executive[this.pageVisitMethodName](this.entityType, this.entityId, this.$route.path)
				} else {
					// Legacy/specific methods expect entityId directly
					await executive[this.pageVisitMethodName](this.entityId, this.$route.path)
				}
				
				this.closeWizard()
			} catch (error) {
				console.error(`Error marking ${this.entityType} changes as read:`, error)
				this.closeWizard()
			}
		},
		
		closeWizard() {
			this.showDialog = false
			this.$emit('close')
		},
		
		getEntityDisplayName() {
			const entityDisplayNames = {
				[EntityType.CONTRACTOR]: this.$t('text.contractor') || 'Contractor',
				[EntityType.CONTRACT_TEMPLATE]: this.$t('text.contractTemplate') || 'Contract Template'
			}
			return entityDisplayNames[this.entityType] || this.entityType
		},
		
		getChangesIntroText() {
			const entityDisplayName = this.getEntityDisplayName().toLowerCase()
			const count = this.displayChanges.length
			
			// Try to get entity-specific translation, fallback to generic
			const entitySpecificKey = `text.${this.entityType.toLowerCase()}ChangesIntro`
			const genericKey = 'text.entityChangesIntro'
			
			return this.$t(entitySpecificKey, { count, entity: entityDisplayName }) ||
					this.$t(genericKey, { count, entity: entityDisplayName }) ||
					this.$t('text.contractorChangesIntro', { count }) ||
					`Found ${count} recent changes to this ${entityDisplayName}.`
		},
		
		getUserInitials(email) {
			if (!email) return '?'
			const parts = email.split('@')[0].split('.')
			if (parts.length >= 2) {
				return (parts[0][0] + parts[1][0]).toUpperCase()
			}
			return email[0].toUpperCase()
		},
		
		getUserRoleText(role) {
			const roleMap = {
				operator: this.$t('text.operator'),
				serviceprovider: this.$t('text.serviceProvider')
			}
			return roleMap[role] || role
		},
		
		getFieldDisplayName(field) {
			// Extract the property name from nested properties
			// For example: 'businessProfileData.bankName' -> 'bankName'
			let propertyName = field
			if (field.includes('.')) {
				const parts = field.split('.')
				propertyName = parts[parts.length - 1] // Get the last part
			}
			
			// Try to find translation in the store
			const currentLocale = this.$store?.state?.selectedLocale || 'en'
			const localeTranslations = this.$store?.state?.translations?.find(t => t.key === currentLocale)?.value || {}
			
			// Check for direct field translation first (for nested properties like businessProfileData.bankName -> bankName)
			if (localeTranslations[propertyName]) {
				return localeTranslations[propertyName]
			}
			
			// Check for full field path translation (for exact matches)
			if (localeTranslations[field]) {
				return localeTranslations[field]
			}
			
			// Try common translation keys that might exist
			const commonKeys = [
				`text_${propertyName}`, // Common pattern: text_fieldName
				`uni_str${propertyName}`, // Common pattern: uni_strFieldName  
				`${propertyName}`, // Direct field name
				propertyName.toLowerCase(),
				propertyName.charAt(0).toUpperCase() + propertyName.slice(1) // Capitalize first letter
			]
			
			for (const key of commonKeys) {
				if (localeTranslations[key]) {
					return localeTranslations[key]
				}
			}
			
			// Fallback: Convert camelCase to readable format
			return this.camelCaseToReadable(propertyName)
		},
		
		camelCaseToReadable(str) {
			// Convert camelCase to space-separated words
			// Examples: 
			// 'bankName' -> 'Bank Name'
			// 'businessOwnerDateOfBirth' -> 'Business Owner Date Of Birth'
			// 'uidNumber' -> 'Uid Number'
			// 'needsContract' -> 'Needs Contract'
			
			return str
				// Insert space before uppercase letters (except the first character)
				.replace(/([a-z])([A-Z])/g, '$1 $2')
				// Split by numbers and letters
				.replace(/([a-zA-Z])(\d)/g, '$1 $2')
				.replace(/(\d)([a-zA-Z])/g, '$1 $2')
				// Capitalize first letter of each word
				.split(' ')
				.map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
				.join(' ')
		},
		
		formatDate(dateString) {
			return moment(dateString).format('DD.MM.YYYY HH:mm')
		},
		
		getLocalizedSummary(summary) {
			// Handle both old text format and new multilingual format
			if (!summary) return ''
			
			// If it's already a string (old format), return as is
			if (typeof summary === 'string') {
				return summary
			}
			
			// If it's an object (new multilingual format), get the appropriate language
			if (typeof summary === 'object') {
				const currentLocale = this.$store.state.selectedLocale || 'en'
				
				// Try to get the summary in the current locale, fallback to English, then any available language
				return summary[currentLocale] || 
				       summary.en || 
				       summary[Object.keys(summary)[0]] || 
				       'No summary available'
			}
			
			return 'No summary available'
		}
	}
}
</script>

<style lang="scss" scoped>
.entity-changes-wizard {
	.wizard-header {
		display: flex;
		align-items: center;
		justify-content: space-between;
		padding: 20px 24px 16px 24px;
		border-bottom: 1px solid #e0e0e0;
		
		.header-content {
			display: flex;
			align-items: center;
			gap: 12px;
			flex: 1;
			
			.title-text {
				font-size: 18px;
				font-weight: 600;
			}
			
			.beta-chip {
				font-size: 10px;
				font-weight: 700;
			}
		}

		.close-button {
			flex-shrink: 0;
		}
	}
	
	.wizard-content {
		padding: 24px;
		max-height: 500px;
		overflow-y: auto;
		
		.loading-container {
			display: flex;
			flex-direction: column;
			align-items: center;
			gap: 16px;
			padding: 40px 0;
		}
		
		.no-changes {
			text-align: center;
			padding: 40px 0;
			color: #666;
			
			p {
				margin-top: 16px;
				font-size: 16px;
			}
		}
		
		.changes-list {
			.changes-intro {
				margin-bottom: 24px;
				font-size: 14px;
				color: #666;
			}
			
			.change-item {
				border: 1px solid #e0e0e0;
				border-radius: 8px;
				padding: 16px;
				margin-bottom: 16px;
				
				.change-header {
					display: flex;
					justify-content: space-between;
					align-items: center;
					margin-bottom: 12px;
					
					.user-info {
						display: flex;
						align-items: center;
						gap: 12px;
						
						.user-details {
							display: flex;
							flex-direction: column;
							
							.user-email {
								font-size: 14px;
								font-weight: 500;
							}
							
							.user-role {
								font-size: 12px;
								color: #666;
							}
						}
					}
					
					.change-time {
						font-size: 12px;
						color: #666;
					}
				}
				
				.change-summary {
					margin-bottom: 12px;
					font-size: 14px;
					line-height: 1.4;
					
					.summary-text {
						white-space: pre-line;
					}
				}
				
				.changed-fields {
					display: flex;
					flex-wrap: wrap;
					gap: 6px;
					
					.field-chip {
						font-size: 11px;
					}
				}
			}
		}
	}
	
	.wizard-actions {
		padding: 16px 24px 20px 24px;
		border-top: 1px solid #e0e0e0;
	}
	
	.avatar-text {
		font-size: 12px;
		font-weight: 600;
	}
}
</style> 